import { batch } from "@legendapp/state";

import { logger } from "~/libs/logger";
import { trpc } from "~/libs/trpc.lib";
import { blocks$ } from "../blocks/blocks.crud";
import { boardFilters$ } from "../board-filters/board-filters.crud";
import { boards$ } from "../boards/boards.crud";
import type { Board } from "../boards/boards.types";
import { ensureDatasetPayload } from "../datasets/datasets.actions";
import { datasets$ } from "../datasets/datasets.crud";
import type { Dataset } from "../datasets/datasets.types";
import type {
  RunPayloadScope,
  RunPayloadUrlResolver,
} from "../datasets/rift-dataset-payload.query";
import { hostOrgs$ } from "./host-orgs.store";
import { clearSnapshotOrigin, markSnapshotOrigin } from "./snapshot-origins";
import {
  clearSnapshotPayloadContext,
  registerSnapshotPayloadContext,
} from "./snapshot-payload-context";

/** Read-only viewer flavour. Decides which tRPC procedure feeds the hydrator. */
export type BoardReadOnlyVariant = "public" | "member";

type BoardSnapshot = Awaited<
  ReturnType<typeof trpc.publicBoards.getSnapshot.query>
>;

/**
 * Per-board cleanup hook returned by `mountBoardSnapshot`. Removes all
 * snapshot rows from the unified stores, clears origin tags, aborts in-flight
 * payload loads, and drops the host-org chip.
 */
type SnapshotCleanup = () => void;

/** Active payload-load abort controllers keyed by board id. */
const payloadControllers = new Map<string, AbortController>();

/** Creates a board-scoped signed-URL resolver for read-only run-result fetches. */
export const createReadOnlyPayloadUrlResolver = (
  boardId: string,
  variant: BoardReadOnlyVariant,
): RunPayloadUrlResolver => {
  return (runId, signal) =>
    variant === "member"
      ? trpc.sharedBoards.getResultUrl.query({ boardId, runId }, { signal })
      : trpc.publicBoards.getResultUrl.query({ boardId, runId }, { signal });
};

/** The run-payload cache scope isolating one read-only board's fetches. */
const readOnlyPayloadScope = (
  boardId: string,
  variant: BoardReadOnlyVariant,
): RunPayloadScope =>
  variant === "member" ? `shared:${boardId}` : `public:${boardId}`;

/**
 * Fills the snapshot's stripped dataset metadata back out to the full
 * `Dataset` shape expected by `datasets$`. The fields we synthesise are
 * never read in read-only mode (writes/queries are gated by capabilities)
 * but TypeScript needs them present.
 */
const padDataset = (
  organizationId: string,
  d: BoardSnapshot["datasets"][number],
): Dataset => {
  const now = new Date();
  return {
    id: d.id,
    name: d.name,
    avatar: d.avatar,
    columns: d.columns ?? {},
    columnsConfig: d.columnsConfig ?? {},
    riftConfig: d.riftConfig ?? null,
    runId: d.runId,
    fetchedAt: d.fetchedAt,
    organizationId,
    datasourceId: null,
    ownerId: "",
    query: "",
    draftQuery: "",
    private: false,
    favorite: false,
    verified: false,
    order: "a0",
    status: "IDLE",
    createdAt: now,
    updatedAt: now,
  } as Dataset;
};

/**
 * Converts the snapshot's board payload (which carries a host-org chip)
 * into the canonical `Board` shape. Snapshot fields we don't expose
 * (`ownerId`, `favorite`, `order`, `createdAt`) get safe defaults.
 */
const padBoard = (b: BoardSnapshot["board"]): Board => {
  return {
    id: b.id,
    name: b.name,
    description: b.description,
    avatar: b.avatar,
    organizationId: b.organizationId,
    public: b.public,
    shareAccess: b.shareAccess,
    updatedAt: b.updatedAt,
    ownerId: "",
    favorite: false,
    order: "a0",
    createdAt: b.updatedAt,
  } as Board;
};

/**
 * Hydrates the unified stores from a board snapshot. Every row written
 * here is tagged in `snapshotOrigins$` so the CRUD wrappers skip remote
 * mutations and Ably echoes that would target rows the viewer doesn't own.
 */
const hydrateSnapshot = (snapshot: BoardSnapshot): void => {
  batch(() => {
    const board = padBoard(snapshot.board);
    markSnapshotOrigin(board.id);
    boards$[board.id].set(board);
    hostOrgs$[board.id].set({
      name: snapshot.board.organizationName,
      logo: snapshot.board.organizationLogo,
    });

    for (const block of snapshot.blocks) {
      markSnapshotOrigin(block.id);
      blocks$[block.id].set(block);
    }

    for (const filter of snapshot.filters) {
      markSnapshotOrigin(filter.id);
      boardFilters$[filter.id].set(filter);
    }

    for (const dataset of snapshot.datasets) {
      markSnapshotOrigin(dataset.id);
      datasets$[dataset.id].set(padDataset(board.organizationId, dataset));
    }
  });
};

/**
 * Removes everything `hydrateSnapshot` wrote for `boardId`. Called from
 * the cleanup hook so a viewer leaving a foreign-org board doesn't leave
 * its rows in the unified stores.
 */
const purgeSnapshot = (
  snapshot: BoardSnapshot,
  boardId: string,
  variant: BoardReadOnlyVariant,
): void => {
  const payloadScope = readOnlyPayloadScope(boardId, variant);
  batch(() => {
    clearSnapshotOrigin(snapshot.board.id);
    boards$[snapshot.board.id].delete();
    hostOrgs$[boardId].delete();

    for (const block of snapshot.blocks) {
      clearSnapshotOrigin(block.id);
      blocks$[block.id].delete();
    }

    for (const filter of snapshot.filters) {
      clearSnapshotOrigin(filter.id);
      boardFilters$[filter.id].delete();
    }

    for (const dataset of snapshot.datasets) {
      clearSnapshotOrigin(dataset.id);
      clearSnapshotPayloadContext(dataset.id, payloadScope);
      datasets$[dataset.id].delete();
    }
  });
};

/**
 * Loads every dataset referenced by the snapshot into DuckDB via the
 * read-only payload routes through the shared payload-query pipeline so OPFS
 * caching and subsequent rift queries work unchanged.
 */
const mountSnapshotPayloads = (
  boardId: string,
  variant: BoardReadOnlyVariant,
  datasetIds: string[],
): void => {
  unmountSnapshotPayloads(boardId);

  if (datasetIds.length === 0) return;

  const controller = new AbortController();
  payloadControllers.set(boardId, controller);

  const payloadUrlResolver = createReadOnlyPayloadUrlResolver(boardId, variant);
  const payloadScope = readOnlyPayloadScope(boardId, variant);

  // Registered before the first ensure so the blocks that mount off the freshly
  // hydrated stores resolve their own payloads through the read-only route
  // instead of the org-scoped default.
  for (const datasetId of datasetIds) {
    registerSnapshotPayloadContext(datasetId, {
      scope: payloadScope,
      urlResolver: payloadUrlResolver,
    });
  }

  for (const datasetId of datasetIds) {
    ensureDatasetPayload(datasetId, {
      signal: controller.signal,
      scope: payloadScope,
      urlResolver: payloadUrlResolver,
    }).catch((err: unknown) => {
      if (controller.signal.aborted) return;
      logger.error(
        { err, datasetId, boardId, variant },
        "read-only payload load failed",
      );
    });
  }
};

/** Aborts in-flight read-only payload loads for a board. */
const unmountSnapshotPayloads = (boardId: string): void => {
  const controller = payloadControllers.get(boardId);
  if (controller) {
    controller.abort();
    payloadControllers.delete(boardId);
  }
};

/**
 * Per-board state for ref-counted snapshot mounts. Shared by all concurrent
 * callers of `mountBoardSnapshot` for the same `boardId` so React StrictMode's
 * mount → cleanup → mount cycle does not produce two competing fetches whose
 * teardowns race and wipe freshly-hydrated rows.
 */
type SnapshotMountEntry = {
  refs: number;
  variant: BoardReadOnlyVariant;
  promise: Promise<BoardSnapshot>;
  snapshot?: BoardSnapshot;
};

const snapshotMounts = new Map<string, SnapshotMountEntry>();

/**
 * Mount lifecycle for a read-only board view. Fetches the snapshot,
 * hydrates the unified stores under snapshot-origin tags, and kicks off
 * Arrow payload loads. Returns a cleanup hook that decrements the
 * board's mount ref count; the underlying stores are only torn down
 * when the last caller releases its handle.
 */
export const mountBoardSnapshot = async (
  boardId: string,
  variant: BoardReadOnlyVariant,
): Promise<SnapshotCleanup> => {
  let entry = snapshotMounts.get(boardId);

  if (entry) {
    entry.refs += 1;
    if (entry.variant !== variant) {
      logger.warn(
        { boardId, existing: entry.variant, requested: variant },
        "snapshot mount variant mismatch; reusing existing in-flight variant",
      );
    }
  } else {
    const promise = (
      variant === "member"
        ? trpc.sharedBoards.getSnapshot.query({ boardId })
        : trpc.publicBoards.getSnapshot.query({ boardId })
    ) as Promise<BoardSnapshot>;

    const newEntry: SnapshotMountEntry = {
      refs: 1,
      variant,
      promise,
    };
    snapshotMounts.set(boardId, newEntry);
    entry = newEntry;

    promise
      .then((snapshot) => {
        // Bail if every caller tore down before the fetch resolved.
        if (snapshotMounts.get(boardId) !== newEntry) return;
        newEntry.snapshot = snapshot;
        hydrateSnapshot(snapshot);
        mountSnapshotPayloads(
          boardId,
          variant,
          snapshot.datasets.map((d) => d.id),
        );
      })
      .catch(() => {
        // Drop the failed entry so the next mount can retry the fetch.
        if (snapshotMounts.get(boardId) === newEntry) {
          snapshotMounts.delete(boardId);
        }
      });
  }

  const mountedEntry = entry;

  await mountedEntry.promise;

  let released = false;
  return () => {
    if (released) return;
    released = true;

    mountedEntry.refs -= 1;
    if (mountedEntry.refs > 0) return;

    // Only purge if this is still the live entry for the board — a failed
    // fetch may have cleared the map already, and we shouldn't tear down a
    // newer mount started after ours.
    if (snapshotMounts.get(boardId) !== mountedEntry) return;
    snapshotMounts.delete(boardId);

    unmountSnapshotPayloads(boardId);
    if (mountedEntry.snapshot) {
      purgeSnapshot(mountedEntry.snapshot, boardId, mountedEntry.variant);
    }
  };
};
